import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { X as XIcon, Signature, Trash, FloppyDisk, Printer, ArrowRight, CheckCircle, ArrowsOut } from "@phosphor-icons/react";

/**
 * SignatureModal
 *
 * A canvas-based signature capture modal that supports mouse, touch and
 * pen/stylus/tablet via Pointer Events. On save, exports the canvas as a
 * base64 PNG and POSTs it to /api/movements/{id}/sign?role=<role>.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   movementId: string
 *   role: "destinatario" | "incaricato"
 *   title?: string
 *   onSigned?: (payload) => void   Called after successful save
 */
export default function SignatureModal({
  open, onClose, movementId, role,
  title, onSigned, movementType, ddtNumber,
}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signerName, setSignerName] = useState("");
  // For exit movements: default true → incaricato = destinatario (single signature)
  const [samePerson, setSamePerson] = useState(true);
  // Fullscreen tablet-friendly signing overlay. When true, a separate canvas
  // takes the whole viewport so users on displayless graphic tablets don't
  // accidentally leave the drawing area.
  const [fullscreen, setFullscreen] = useState(false);
  const fsCanvasRef = useRef(null);
  const fsCtxRef = useRef(null);
  const fsDrawing = useRef(false);
  const [fsDirty, setFsDirty] = useState(false);
  // Multi-step state machine (only used when samePerson === false on exit movements):
  //   "start"         → initial (either single-sign combined OR sign as incaricato when samePerson=false)
  //   "choice"        → after incaricato saved, ask: destinatario digital vs paper
  //   "destinatario"  → sign destinatario digitally
  //   "paper"         → paper info screen with quick DDT link
  const [step, setStep] = useState("start");

  const isExit = movementType === "exit";

  // Determine what role we are currently capturing.
  const captureRole = step === "destinatario"
    ? "destinatario"
    : (step === "start" && isExit && !samePerson)
      ? "incaricato"
      : role;
  const captureLabel = captureRole === "destinatario" ? "Destinatario" : "Incaricato del trasporto";
  const label = captureLabel;

  useEffect(() => {
    if (!open) return;
    setIsDirty(false);
    setSignerName("");
    setSamePerson(true);
    setStep("start");
    setFullscreen(false);
    setFsDirty(false);
  }, [open]);

  // (Re)initialise the canvas whenever the sign UI becomes visible
  // (on open, and again after a step transition into a sign step).
  useEffect(() => {
    if (!open) return;
    if (step !== "start" && step !== "destinatario") return;
    setIsDirty(false);
    requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111111";
      ctx.lineWidth = 2.2;
      ctxRef.current = ctx;
    });
  }, [open, step]);

  const posFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    const { x, y } = posFromEvent(e);
    const ctx = ctxRef.current;
    if (!ctx) return;
    // For pen/tablet input, use pressure-sensitive line width for natural feel
    if ((e.pointerType === "pen" || e.pointerType === "touch") && e.pressure > 0) {
      ctx.lineWidth = 0.8 + e.pressure * 3.5;
    } else {
      ctx.lineWidth = 2.2;
    }
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
  };

  const onPointerMove = (e) => {
    if (!drawing.current) return;
    const { x, y } = posFromEvent(e);
    const ctx = ctxRef.current;
    if ((e.pointerType === "pen" || e.pointerType === "touch") && e.pressure > 0) {
      ctx.lineWidth = 0.8 + e.pressure * 3.5;
    }
    ctx.lineTo(x, y);
    ctx.stroke();
    setIsDirty(true);
  };

  const onPointerUp = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setIsDirty(false);
  };

  // ---------- Fullscreen (tavoletta) mode ----------
  // Initialise the fullscreen canvas when it becomes visible so its backing
  // store matches the viewport size at correct DPR.
  useEffect(() => {
    if (!fullscreen) return;
    setFsDirty(false);
    requestAnimationFrame(() => {
      const canvas = fsCanvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111111";
      ctx.lineWidth = 2.4;
      fsCtxRef.current = ctx;
    });
    // Lock body scroll while in fullscreen so touch-scroll can't sneak in.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [fullscreen]);

  const fsPos = (e) => {
    const rect = fsCanvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onFsPointerDown = (e) => {
    e.preventDefault();
    fsCanvasRef.current.setPointerCapture(e.pointerId);
    const { x, y } = fsPos(e);
    const ctx = fsCtxRef.current;
    if (!ctx) return;
    if ((e.pointerType === "pen" || e.pointerType === "touch") && e.pressure > 0) {
      ctx.lineWidth = 1.0 + e.pressure * 4.0;
    } else {
      ctx.lineWidth = 2.4;
    }
    ctx.beginPath(); ctx.moveTo(x, y);
    fsDrawing.current = true;
  };
  const onFsPointerMove = (e) => {
    if (!fsDrawing.current) return;
    const { x, y } = fsPos(e);
    const ctx = fsCtxRef.current;
    if ((e.pointerType === "pen" || e.pointerType === "touch") && e.pressure > 0) {
      ctx.lineWidth = 1.0 + e.pressure * 4.0;
    }
    ctx.lineTo(x, y); ctx.stroke();
    setFsDirty(true);
  };
  const onFsPointerUp = (e) => {
    if (!fsDrawing.current) return;
    fsDrawing.current = false;
    try { fsCanvasRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const clearFsCanvas = () => {
    const c = fsCanvasRef.current, ctx = fsCtxRef.current;
    if (!c || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, c.width / dpr, c.height / dpr);
    setFsDirty(false);
  };
  // Find the bounding box of actually-drawn pixels on a canvas so we can
  // crop the whitespace away. Returns the full canvas rect when the drawing
  // couldn't be inspected (e.g. CORS-tainted) or no pixel is above the
  // alpha threshold.
  const measureInkBounds = (canvas, padding = 24) => {
    const w = canvas.width, h = canvas.height;
    try {
      const ctx = canvas.getContext("2d");
      const { data } = ctx.getImageData(0, 0, w, h);
      let minX = w, minY = h, maxX = -1, maxY = -1;
      // Scan every 2nd pixel: fast enough for 4K canvases, loses nothing
      // meaningful for a human signature (strokes are >> 2px).
      for (let y = 0; y < h; y += 2) {
        const row = y * w * 4;
        for (let x = 0; x < w; x += 2) {
          if (data[row + x * 4 + 3] > 20) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return { x: 0, y: 0, w, h }; // empty — bail out
      // Add some breathing room around the strokes
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(w - 1, maxX + padding);
      maxY = Math.min(h - 1, maxY + padding);
      return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    } catch {
      return { x: 0, y: 0, w, h };
    }
  };

  // Confirm fullscreen drawing → crop whitespace, then paint the ink area
  // (scaled, fit inside) into the small canvas that shows in the modal and
  // close fullscreen. Without the crop a signature traced in only part of
  // the fullscreen area would be rendered tiny in the destination (and in
  // the PDF).
  const confirmFullscreen = () => {
    if (!fsDirty) { toast.error("Traccia una firma prima di confermare"); return; }
    const src = fsCanvasRef.current;
    const dst = canvasRef.current;
    const dstCtx = ctxRef.current;
    if (!src || !dst || !dstCtx) { setFullscreen(false); return; }
    const dpr = window.devicePixelRatio || 1;
    const dstW = dst.width / dpr, dstH = dst.height / dpr;
    dstCtx.clearRect(0, 0, dstW, dstH);
    // Crop to actual ink
    const b = measureInkBounds(src);
    const scale = Math.min(dstW / b.w, dstH / b.h);
    const drawW = b.w * scale, drawH = b.h * scale;
    const offX = (dstW - drawW) / 2, offY = (dstH - drawH) / 2;
    dstCtx.drawImage(src, b.x, b.y, b.w, b.h, offX, offY, drawW, drawH);
    setIsDirty(true);
    setFullscreen(false);
  };

  // Send the "signed PDF" email — best-effort, silently ignored when not applicable
  const sendEmail = async () => {
    try {
      const emailRes = await api.post(`/movements/${movementId}/send-email`, {});
      toast.success(`📧 Email inviata a ${emailRes.data.to}`);
    } catch (emailErr) {
      const msg = emailErr?.response?.data?.detail || "";
      if (msg && !/non applicabile|Nessun destinatario|non configurato/.test(msg)) {
        toast.error(`Firma OK, ma email non inviata: ${msg}`);
      }
    }
  };

  const save = async () => {
    if (!isDirty) {
      toast.error("Traccia una firma prima di salvare");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const useApplyBoth = isExit && samePerson && step === "start" && captureRole === "destinatario";
      const { data } = await api.post(`/movements/${movementId}/sign`, {
        role: captureRole,
        image: dataUrl,
        signer_name: signerName || null,
        apply_to_both: useApplyBoth,
      });
      toast.success(`Firma "${captureLabel}" acquisita`);
      // Wait for the parent to refresh its state before we close — otherwise the
      // list may render stale rows for multi-product groups where the freshly
      // signed row would still appear "to sign" until a manual reload.
      if (onSigned) { try { await onSigned(data); } catch { /* ignore */ } }

      // Branch: multi-step flow when samePerson is unchecked on an exit movement
      if (isExit && !samePerson && step === "start") {
        // Just saved the incaricato signature → ask about destinatario mode
        setStep("choice");
        setSaving(false);
        return;
      }

      // Otherwise the flow is complete → send email + close
      await sendEmail();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Errore salvataggio firma");
    } finally {
      setSaving(false);
    }
  };

  const chooseDigital = () => {
    setSignerName("");
    setStep("destinatario");
  };

  const choosePaper = async () => {
    // Notify server that the destinatario will sign on paper (no digital signature captured)
    // We simply move to the paper info screen — email is skipped because the destinatario has
    // not signed digitally yet. The DDT is available for print from the /ddt section.
    setStep("paper");
  };

  // Bypass: skip digital signature and download the PDF for manual signing on paper
  const skipAndDownload = async () => {
    setSaving(true);
    try {
      const t = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/movements/${movementId}/pdf`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documento-firma-manuale-${movementId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato. Stampa e firma a mano.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Errore download PDF");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // Dynamic title based on step
  const modalTitle = title || (
    step === "choice" ? "Come firma il destinatario?"
      : step === "paper" ? "Firma cartacea destinatario"
        : step === "destinatario" ? "Firma · Destinatario (2 di 2)"
          : (isExit && !samePerson) ? "Firma · Incaricato del trasporto (1 di 2)"
            : `Firma · ${label}`
  );

  const isSignStep = step === "start" || step === "destinatario";
  const nameInputVisible = isSignStep;
  const samePersonVisible = step === "start" && isExit && role === "destinatario";

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-2 md:p-4" data-testid="signature-modal">
      <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-2xl">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Signature size={20} weight="bold" className="text-[#002FA7]" />
            <h3 data-testid="signature-title" className="font-display font-bold text-lg tracking-tight">
              {modalTitle}
            </h3>
          </div>
          <button onClick={onClose} data-testid="signature-close" className="p-1.5 hover:bg-neutral-100 transition-colors">
            <XIcon size={18} />
          </button>
        </div>

        {/* ---------- CHOICE STEP: destinatario digital vs paper ---------- */}
        {step === "choice" && (
          <div className="p-6 space-y-4" data-testid="signature-choice">
            <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
              <CheckCircle size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
              <div>
                Firma dell&apos;incaricato del trasporto acquisita con successo.
                <div className="text-xs text-emerald-700 mt-0.5">Ora scegli come procedere per la firma del destinatario.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <button
                data-testid="signature-choice-digital"
                onClick={chooseDigital}
                className="flex flex-col items-center gap-2 p-5 border-2 border-[#002FA7] bg-white hover:bg-[#002FA7] hover:text-white text-[#002FA7] transition-colors"
              >
                <Signature size={32} weight="bold" />
                <div className="font-semibold text-sm uppercase tracking-wider">Firma digitale</div>
                <div className="text-xs opacity-80 text-center">Il destinatario firma ora su schermo/tablet</div>
              </button>
              <button
                data-testid="signature-choice-paper"
                onClick={choosePaper}
                className="flex flex-col items-center gap-2 p-5 border-2 border-neutral-300 bg-white hover:bg-neutral-100 text-neutral-700 transition-colors"
              >
                <Printer size={32} weight="bold" />
                <div className="font-semibold text-sm uppercase tracking-wider">Firma cartacea</div>
                <div className="text-xs opacity-80 text-center">Stampa il DDT e fai firmare a mano</div>
              </button>
            </div>
          </div>
        )}

        {/* ---------- PAPER STEP: info + quick link to /ddt ---------- */}
        {step === "paper" && (
          <div className="p-6 space-y-4" data-testid="signature-paper-info">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <Printer size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">Firma cartacea selezionata.</div>
                <div className="text-xs mt-1">
                  Una versione PDF del DDT{ddtNumber ? <> <span className="font-mono">{ddtNumber}</span></> : ""} è disponibile
                  nella sezione <b>DDT</b> e può essere stampata per la firma cartacea del destinatario.
                  Dopo la firma su carta, potrai caricare la scansione dalla stessa sezione tramite il pulsante <b>Carica scansione</b>.
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2 justify-end">
              <button
                data-testid="signature-paper-close"
                onClick={onClose}
                className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-50 text-neutral-700 transition-colors"
              >
                Ho capito, chiudi
              </button>
              <Link
                to="/ddt"
                data-testid="signature-paper-go-ddt"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-[#002FA7] hover:bg-[#002480] text-white transition-colors"
              >
                Vai al DDT
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        )}

        {/* ---------- SIGN STEP: canvas capture ---------- */}
        {isSignStep && (
          <div className="p-4 space-y-3">
            {nameInputVisible && (
              <div>
                <label className="label-eyebrow block mb-1">Nome firmatario (opzionale)</label>
                <input
                  data-testid="signature-signer-name"
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Chi sta firmando? (es. Mario Rossi)"
                  className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </div>
            )}

            {samePersonVisible && (
              <label
                data-testid="signature-same-person-wrapper"
                className="flex items-start gap-2 p-3 bg-[#002FA7]/5 border border-[#002FA7]/20 cursor-pointer select-none"
              >
                <input
                  data-testid="signature-same-person-checkbox"
                  type="checkbox"
                  checked={samePerson}
                  onChange={(e) => { setSamePerson(e.target.checked); setSignerName(""); }}
                  className="mt-0.5 h-4 w-4 accent-[#002FA7] cursor-pointer"
                />
                <div className="text-xs leading-snug">
                  <div className="font-semibold text-neutral-900">Incaricato del trasporto e destinatario sono la stessa persona</div>
                  <div className="text-neutral-600 mt-0.5">Se attivo, questa firma verrà applicata anche come firma dell&apos;incaricato del trasporto.</div>
                </div>
              </label>
            )}

            <div>
              <div className="label-eyebrow mb-1">Firma di <span className="text-[#002FA7]">{captureLabel}</span> con dito, penna, mouse o tavoletta grafica</div>
              <div className="relative border-2 border-dashed border-neutral-300 bg-white" style={{ touchAction: "none" }}>
                <canvas
                  ref={canvasRef}
                  data-testid="signature-canvas"
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  onPointerLeave={onPointerUp}
                  className="block w-full cursor-crosshair"
                  style={{ height: "220px", touchAction: "none" }}
                />
                {!isDirty && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-neutral-300 text-sm select-none">
                    ✎ Traccia qui
                  </div>
                )}
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Compatibile con Wacom, Huion, XP-Pen e ogni tavoletta grafica standard. Pressione sensibile per un tratto naturale.
              </p>
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  data-testid="signature-clear"
                  onClick={clearCanvas}
                  disabled={!isDirty || saving}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-neutral-300 hover:bg-neutral-50 disabled:opacity-40"
                >
                  <Trash size={14} weight="bold" /> Pulisci
                </button>
                <button
                  type="button"
                  data-testid="signature-fullscreen-open"
                  onClick={() => setFullscreen(true)}
                  disabled={saving}
                  title="Apri un'area di firma a tutto schermo — ideale per tavolette senza display"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 disabled:opacity-40"
                >
                  <ArrowsOut size={14} weight="bold" /> Firma con tavoletta
                </button>
              </div>
              <button
                data-testid="signature-save"
                onClick={save}
                disabled={!isDirty || saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#002FA7] hover:bg-[#002480] disabled:opacity-40 text-white transition-colors"
              >
                <FloppyDisk size={16} weight="bold" />
                {saving ? "Salvataggio..." : (isExit && !samePerson && step === "start") ? "Salva e continua" : "Salva firma"}
              </button>
            </div>

            {step === "start" && (
              <div className="border-t border-neutral-200 pt-3 mt-2">
                <button
                  data-testid="signature-skip-manual"
                  onClick={skipAndDownload}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-50 disabled:opacity-40 text-neutral-700 transition-colors"
                >
                  <Printer size={16} weight="bold" />
                  Stampa e firma a mano (bypass in caso di problemi tecnici)
                </button>
                <p className="text-[11px] text-neutral-400 text-center mt-1.5">
                  Scarica il PDF senza firma digitale — utile se la tavoletta/schermo non risponde.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {/* ---------- Fullscreen tablet-friendly overlay ---------- */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[60] bg-white flex flex-col"
          data-testid="signature-fullscreen-overlay"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-300 bg-neutral-50 shrink-0">
            <div>
              <div className="label-eyebrow text-[#002FA7]">Firma tavoletta · {captureLabel}</div>
              <div className="text-xs text-neutral-600">
                Firma in tutta l'area sotto. Al termine premi <b>Conferma</b>.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="signature-fullscreen-clear"
                onClick={clearFsCanvas}
                disabled={!fsDirty}
                className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-neutral-300 hover:bg-neutral-50 disabled:opacity-40"
              >
                <Trash size={14} weight="bold" /> Pulisci
              </button>
              <button
                type="button"
                data-testid="signature-fullscreen-cancel"
                onClick={() => setFullscreen(false)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-neutral-400 text-neutral-700 hover:bg-neutral-100"
              >
                <XIcon size={14} weight="bold" /> Esci
              </button>
              <button
                type="button"
                data-testid="signature-fullscreen-confirm"
                onClick={confirmFullscreen}
                disabled={!fsDirty}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#002FA7] hover:bg-[#002480] disabled:opacity-40 text-white"
              >
                <CheckCircle size={16} weight="bold" /> Conferma
              </button>
            </div>
          </div>
          <div className="flex-1 relative bg-white" style={{ touchAction: "none" }}>
            <canvas
              ref={fsCanvasRef}
              data-testid="signature-fullscreen-canvas"
              onPointerDown={onFsPointerDown}
              onPointerMove={onFsPointerMove}
              onPointerUp={onFsPointerUp}
              onPointerCancel={onFsPointerUp}
              onPointerLeave={onFsPointerUp}
              className="absolute inset-0 w-full h-full cursor-crosshair"
              style={{ touchAction: "none" }}
            />
            {!fsDirty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-neutral-200 select-none">
                <span className="text-4xl md:text-6xl font-light">✎ Traccia qui</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
